import React, { useState, useEffect } from 'react'
import {
  UserIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  ClockIcon,
  CurrencyDollarIcon,
  EyeIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalendarIcon,
  MapPinIcon
} from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../config/api'
import { readApiAccessMessage } from '../utils/apiAccessError'

import { formatSydneyDateTime } from '../utils/sydneyDateTime'
const ExecutiveDashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    profiles: {
      totalProfiles: 0,
      totalVisitors: 0,
      totalMembers: 0,
      newMembersThisMonth: 0,
      newVisitorsThisMonth: 0,
      highRiskMembers: 0
    },
    kyc: {
      totalKyc: 0,
      kycThisMonth: 0
    },
    aml: {
      totalScreenings: 0,
      screeningsThisMonth: 0,
      lastUpdate: null,
      lastPersonUpdate: null,
      lastEntityUpdate: null,
      totalPersons: 0,
      totalEntities: 0,
      totalPersonAssociations: 0,
      totalEntityAssociations: 0,
      completedBatches: 0,
      dataQuality: null,
      recentBatches: [],
    },
    cases: {
      totalCases: 0,
      casesThisMonth: 0,
      openCases: 0,
      criticalCases: 0
    },
    smr: {
      totalSmr: 0,
      smrThisMonth: 0
    },
    venues: [],
    ecdd: {
      totalEcdd: 0,
      ecddThisMonth: 0
    },
    watchlist: {
      totalWatchlist: 0
    }
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedTimeframe, setSelectedTimeframe] = useState('month')

  useEffect(() => {
    fetchDashboardData()
  }, [selectedTimeframe])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      
      // Fetch data from multiple endpoints
      const [
        profilesRes,
        kycRes,
        amlRes,
        casesRes,
        smrRes,
        venuesRes,
        watchlistRes,
        ecddRes,
      ] = await Promise.allSettled([
        authFetch(getLocalApiUrl('/api/executive/profiles/stats')),
        authFetch(getLocalApiUrl('/api/executive/kyc/stats')),
        authFetch(getLocalApiUrl('/api/aml/dashboard')),
        authFetch(getLocalApiUrl('/api/executive/cases/stats')),
        authFetch(getLocalApiUrl('/api/executive/smr/stats')),
        authFetch(getLocalApiUrl('/api/executive/venues/risk')),
        authFetch(getLocalApiUrl('/api/watchlist/stats')),
        authFetch(getLocalApiUrl('/api/executive/ecdd/stats')),
      ])

      const httpResponses = [
        profilesRes, kycRes, amlRes, casesRes, smrRes, venuesRes, watchlistRes, ecddRes,
      ]
        .filter((s) => s.status === 'fulfilled' && s.value && typeof s.value.status === 'number')
        .map((s) => s.value)
      if (httpResponses.length > 0 && httpResponses.every((r) => r.status === 403 || r.status === 401)) {
        setError(await readApiAccessMessage(httpResponses[0]))
        return
      }

      // Parse all bodies first, then merge via functional setState so React Strict Mode / overlapping
      // requests never overwrite good KPIs with stale initial zeros from `...dashboardData`.
      let profilePatch = null
      if (profilesRes.status === 'fulfilled' && profilesRes.value.ok) {
        try {
          const profileData = await profilesRes.value.json()
          if (profileData.success && profileData.data) profilePatch = profileData.data
        } catch (e) {
          console.warn('Failed to parse profiles data')
        }
      } else if (profilesRes.status === 'fulfilled' && !profilesRes.value.ok) {
        console.warn('Executive /api/executive/profiles/stats HTTP', profilesRes.value.status)
      }

      let kycPatch = null
      if (kycRes.status === 'fulfilled') {
        try {
          const kycData = await kycRes.value.json()
          if (kycData.success && kycData.data) kycPatch = kycData.data
        } catch (e) {
          console.warn('Failed to parse KYC data')
        }
      }

      let amlPatch = null
      if (amlRes.status === 'fulfilled' && amlRes.value.ok) {
        try {
          const amlData = await amlRes.value.json()
          if (amlData.success && amlData.data) {
            const d = amlData.data
            const totalPersons = Number(d.totalPersons) || 0
            const totalEntities = Number(d.totalEntities) || 0
            const apiScreenings = Number(d.totalScreenings)
            amlPatch = {
              ...d,
              totalScreenings:
                Number.isFinite(apiScreenings) && apiScreenings > 0
                  ? apiScreenings
                  : totalPersons + totalEntities,
              screeningsThisMonth: Number(d.screeningsThisMonth) || 0,
              lastPersonUpdate: d.lastPersonUpdate ?? null,
              lastEntityUpdate: d.lastEntityUpdate ?? null,
              lastUpdate: d.lastPersonUpdate || d.lastEntityUpdate || null,
              totalPersons,
              totalEntities,
              totalPersonAssociations: Number(d.totalPersonAssociations) || 0,
              totalEntityAssociations: Number(d.totalEntityAssociations) || 0,
              completedBatches: Number(d.completedBatches) || 0,
              dataQuality: d.dataQuality && typeof d.dataQuality === 'object' ? d.dataQuality : null,
              recentBatches: Array.isArray(d.recentBatches) ? d.recentBatches : [],
            }
          }
        } catch (e) {
          console.warn('Failed to parse AML data')
        }
      }

      let casesPatch = null
      if (casesRes.status === 'fulfilled') {
        try {
          const casesData = await casesRes.value.json()
          if (casesData.success && casesData.data) casesPatch = casesData.data
        } catch (e) {
          console.warn('Failed to parse cases data')
        }
      }

      let smrPatch = null
      if (smrRes.status === 'fulfilled') {
        try {
          const smrData = await smrRes.value.json()
          if (smrData.success && smrData.data) smrPatch = smrData.data
        } catch (e) {
          console.warn('Failed to parse SMR data')
        }
      }

      let venuesList = null
      if (venuesRes.status === 'fulfilled') {
        try {
          const venuesData = await venuesRes.value.json()
          if (venuesData.success && Array.isArray(venuesData.data)) venuesList = venuesData.data
        } catch (e) {
          console.warn('Failed to parse venues data')
        }
      }

      let watchlistPatch = null
      if (watchlistRes.status === 'fulfilled') {
        try {
          const watchlistData = await watchlistRes.value.json()
          if (watchlistData.success && watchlistData.data) watchlistPatch = watchlistData.data
        } catch (e) {
          console.warn('Failed to parse watchlist data')
        }
      }

      let ecddPatch = null
      if (ecddRes.status === 'fulfilled') {
        try {
          const ecddData = await ecddRes.value.json()
          if (ecddData.success && ecddData.data) ecddPatch = ecddData.data
        } catch (e) {
          console.warn('Failed to parse ECDD data')
        }
      }

      setDashboardData((prev) => ({
        ...prev,
        profiles: profilePatch ? { ...prev.profiles, ...profilePatch } : prev.profiles,
        kyc: kycPatch ? { ...prev.kyc, ...kycPatch } : prev.kyc,
        aml: amlPatch ? { ...prev.aml, ...amlPatch } : prev.aml,
        cases: casesPatch ? { ...prev.cases, ...casesPatch } : prev.cases,
        smr: smrPatch ? { ...prev.smr, ...smrPatch } : prev.smr,
        venues: venuesList != null ? venuesList : prev.venues,
        watchlist: watchlistPatch ? { ...prev.watchlist, ...watchlistPatch } : prev.watchlist,
        ecdd: ecddPatch ? { ...prev.ecdd, ...ecddPatch } : prev.ecdd,
      }))
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
      const msg = error?.message || ''
      if (/forbidden|403|permission/i.test(msg)) {
        setError("Access Denied. You don't have permission to access this page.")
      } else {
        setError('Failed to load dashboard data')
      }
    } finally {
      setLoading(false)
    }
  }

  const KPICard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue', onClick }) => (
    <div 
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className={`text-3xl font-bold text-${color}-600 mb-1`}>{value}</p>
          {subtitle && (
            <div className="flex items-center text-sm text-gray-500">
              {trend && (
                trend > 0 ? (
                  <ArrowTrendingUpIcon className="w-4 h-4 text-green-500 mr-1" />
                ) : (
                  <ArrowTrendingDownIcon className="w-4 h-4 text-red-500 mr-1" />
                )
              )}
              <span>{subtitle}</span>
            </div>
          )}
        </div>
        <div className={`w-12 h-12 bg-${color}-100 rounded-lg flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
    </div>
  )

  const VenueRiskCard = ({ venue }) => {
    const getRiskColor = (score) => {
      if (score >= 90) return 'red'
      if (score >= 70) return 'orange'
      if (score >= 40) return 'yellow'
      return 'green'
    }

    const color = getRiskColor(venue.riskScore)

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-900">{venue.name}</h4>
          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full bg-${color}-100 text-${color}-800`}>
            {venue.status}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Risk Score</span>
          <div className="flex items-center">
            <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
              <div 
                className={`h-2 rounded-full bg-${color}-500`}
                style={{ width: `${venue.riskScore}%` }}
              ></div>
            </div>
            <span className="text-sm font-medium text-gray-900">{venue.riskScore}</span>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-psg-teal mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Executive Dashboard...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">
            {/permission|access denied/i.test(error) ? 'Access Denied' : 'Unable to load dashboard'}
          </h2>
          <p className="text-gray-600 mt-2">{error}</p>
          <button 
            onClick={fetchDashboardData}
            className="mt-4 px-4 py-2 bg-psg-teal text-white rounded-lg hover:bg-psg-teal-600"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Safe Metrics */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <UserIcon className="w-6 h-6 text-psg-teal mr-2" />
            Profile Safe - Member & Visitor Management
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
            <KPICard
              title="Total profiles"
              value={(dashboardData.profiles.totalProfiles ?? dashboardData.profiles.totalMembers + dashboardData.profiles.totalVisitors).toLocaleString()}
              subtitle="All patron_profiles (matches Profile dashboard)"
              icon={UserGroupIcon}
              color="indigo"
            />
            <KPICard
              title="Visitor profiles"
              value={dashboardData.profiles.totalVisitors.toLocaleString()}
              subtitle="VISITOR type or member_id like visitor_*"
              icon={UserGroupIcon}
              color="blue"
            />
            <KPICard
              title="Non-visitor profiles"
              value={dashboardData.profiles.totalMembers.toLocaleString()}
              subtitle="Everyone not flagged as visitor"
              icon={UserIcon}
              color="green"
            />
            <KPICard
              title="New Members"
              value={dashboardData.profiles.newMembersThisMonth}
              subtitle="This month"
              icon={UserIcon}
              color="purple"
              trend={1}
            />
            <KPICard
              title="New Visitors"
              value={dashboardData.profiles.newVisitorsThisMonth}
              subtitle="This month"
              icon={UserGroupIcon}
              color="indigo"
              trend={1}
            />
            <KPICard
              title="High Risk Members"
              value={dashboardData.profiles.highRiskMembers}
              subtitle="Requires monitoring"
              icon={ExclamationTriangleIcon}
              color="red"
            />
          </div>
        </div>

        {/* Compliance & Screening Metrics */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <ShieldCheckIcon className="w-6 h-6 text-psg-teal mr-2" />
            Compliance & Screening
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <KPICard
              title="Total KYCs"
              value={dashboardData.kyc.totalKyc.toLocaleString()}
              subtitle={`${dashboardData.kyc.kycThisMonth} this month`}
              icon={DocumentTextIcon}
              color="blue"
              trend={1}
            />
            <KPICard
              title="AML persons + entities"
              value={dashboardData.aml.totalScreenings.toLocaleString()}
              subtitle={`${dashboardData.aml.totalPersons.toLocaleString()} persons · ${dashboardData.aml.totalEntities.toLocaleString()} entities`}
              icon={ShieldCheckIcon}
              color="green"
              trend={1}
            />
            <KPICard
              title="Enhanced Due Diligence"
              value={dashboardData.ecdd.totalEcdd}
              subtitle={`Registry total (all statuses). ${dashboardData.ecdd.ecddThisMonth} with created_at this month`}
              icon={DocumentTextIcon}
              color="purple"
            />
            <KPICard
              title="Watchlist Entries"
              value={dashboardData.watchlist.totalWatchlist}
              subtitle="Active monitoring"
              icon={EyeIcon}
              color="orange"
            />
          </div>

          {(dashboardData.aml.totalPersonAssociations > 0 ||
            dashboardData.aml.totalEntityAssociations > 0 ||
            dashboardData.aml.dataQuality ||
            (dashboardData.aml.completedBatches ?? 0) > 0) && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">AML reference data</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard
                  title="Person associations"
                  value={dashboardData.aml.totalPersonAssociations.toLocaleString()}
                  subtitle="Links in AML reference"
                  icon={UserGroupIcon}
                  color="blue"
                />
                <KPICard
                  title="Entity associations"
                  value={dashboardData.aml.totalEntityAssociations.toLocaleString()}
                  subtitle="Links in AML reference"
                  icon={BuildingOfficeIcon}
                  color="blue"
                />
                <KPICard
                  title="Ingestion batches (done)"
                  value={String(dashboardData.aml.completedBatches ?? 0)}
                  subtitle="Completed load batches"
                  icon={CheckCircleIcon}
                  color="indigo"
                />
                {dashboardData.aml.dataQuality && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex flex-col justify-center">
                    <p className="text-sm font-medium text-gray-600 mb-2">Data quality (reference)</p>
                    <ul className="text-sm text-gray-800 space-y-1">
                      <li>Person names: {dashboardData.aml.dataQuality.personNameCoverage ?? '—'}%</li>
                      <li>Entity addresses: {dashboardData.aml.dataQuality.entityAddressCoverage ?? '—'}%</li>
                      <li>Associations: {dashboardData.aml.dataQuality.associationCompleteness ?? '—'}%</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Monitor Safe & Cases */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <ChartBarIcon className="w-6 h-6 text-psg-teal mr-2" />
            Monitor Safe - Cases & SMR
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <KPICard
              title="Total Cases"
              value={dashboardData.cases.totalCases}
              subtitle={`${dashboardData.cases.casesThisMonth} this month`}
              icon={DocumentTextIcon}
              color="blue"
              trend={1}
            />
            <KPICard
              title="Open Cases"
              value={dashboardData.cases.openCases}
              subtitle="Requires attention"
              icon={ClockIcon}
              color="yellow"
            />
            <KPICard
              title="Critical Cases"
              value={dashboardData.cases.criticalCases}
              subtitle="High priority"
              icon={ExclamationTriangleIcon}
              color="red"
            />
            <KPICard
              title="SMR Submissions"
              value={dashboardData.smr.totalSmr}
              subtitle={`${dashboardData.smr.smrThisMonth} this month`}
              icon={DocumentTextIcon}
              color="green"
              trend={1}
            />
          </div>
        </div>

        {/* Venues Risk Overview */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <BuildingOfficeIcon className="w-6 h-6 text-psg-teal mr-2" />
            Venue Risk Assessment
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {dashboardData.venues.map((venue) => (
              <VenueRiskCard key={venue.id} venue={venue} />
            ))}
          </div>
        </div>

        {/* System Health & Updates */}
        <div className="mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent AML ingestion</h3>
              {dashboardData.aml.recentBatches && dashboardData.aml.recentBatches.length > 0 ? (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {dashboardData.aml.recentBatches.slice(0, 8).map((b, i) => (
                    <div
                      key={`${b.created_at || ''}-${i}`}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-2 border-b border-gray-100 last:border-0 gap-1"
                    >
                      <div>
                        <span className="text-sm font-medium text-gray-800">{b.batch_name || 'Batch'}</span>
                        <span className="text-xs text-gray-500 ml-2">{b.status || ''}</span>
                      </div>
                      <div className="text-xs text-gray-500 sm:text-right">
                        {b.created_at
                          ? formatSydneyDateTime(new Date(b.created_at))
                          : ''}
                        {(b.records_processed != null || b.records_success != null) && (
                          <span className="block text-gray-600">
                            Processed {b.records_processed ?? '—'} · OK {b.records_success ?? '—'}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No recent batch rows returned from the AML API.</p>
              )}
            </div>

            {/* System Status */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">System Status</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">AML Database</span>
                  <div className="flex items-center text-right">
                    <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                    <span className="text-sm text-green-600">
                      {dashboardData.aml.lastPersonUpdate || dashboardData.aml.lastEntityUpdate ? (
                        <>Last update {formatSydneyDateTime(new Date(dashboardData.aml.lastPersonUpdate || dashboardData.aml.lastEntityUpdate))}</>
                      ) : (
                        <>
                          {dashboardData.aml.totalScreenings > 0
                            ? `${dashboardData.aml.totalPersons.toLocaleString()} persons · ${dashboardData.aml.totalEntities.toLocaleString()} entities`
                            : 'No counts yet'}
                        </>
                      )}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Rules Engine</span>
                  <div className="flex items-center">
                    <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                    <span className="text-sm text-green-600">Active</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Transaction Monitoring</span>
                  <div className="flex items-center">
                    <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                    <span className="text-sm text-green-600">Real-time</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Compliance Reporting</span>
                  <div className="flex items-center">
                    <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                    <span className="text-sm text-green-600">Operational</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}

export default ExecutiveDashboard
